import { z } from 'zod'

/** How many people fit into a group a profile runs. */
export const ORGANIZER_GROUP_MAX_PARTICIPANTS = 500

const groupTitle = z.string().trim().min(2).max(80)
const groupDescription = z.string().trim().max(500).nullable().optional()

/** POST /api/businesses/{businessId}/groups */
export const createOrganizerGroupRequestSchema = z.object({
  hostProfileId: z.string().uuid(),
  title: groupTitle,
  description: groupDescription,
  /** Only the business writes; members read and react. */
  onlyBusinessWrites: z.boolean().optional(),
  /** Offered on the profile's page in the app. */
  listed: z.boolean().optional(),
})

/** PATCH /api/businesses/{businessId}/groups/{groupId} */
export const updateOrganizerGroupRequestSchema = z.object({
  title: groupTitle.optional(),
  description: groupDescription,
  onlyBusinessWrites: z.boolean().optional(),
  listed: z.boolean().optional(),
})

/** A group as the business that runs it sees it. */
export const organizerGroupSchema = z.object({
  id: z.string().uuid(),
  profileId: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  onlyBusinessWrites: z.boolean(),
  listed: z.boolean(),
  /** Everyone in the group except the business itself. */
  memberCount: z.number().int(),
  inviteUrl: z.string(),
  lastMessageAt: z.string().nullable(),
  createdAt: z.string(),
})
export type OrganizerGroup = z.infer<typeof organizerGroupSchema>

export const organizerGroupsQuerySchema = z.object({ hostProfileId: z.string().uuid() })

export const organizerGroupsResponseSchema = z.object({
  groups: z.array(organizerGroupSchema),
})

/** POST /api/businesses/{businessId}/groups/{groupId}/messages */
export const sendOrganizerGroupMessageRequestSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), content: z.string().trim().min(1).max(2000) }),
  z.object({ kind: z.literal('event_share'), eventId: z.string().uuid() }),
])

/** A group message as the dashboard shows it. */
export const organizerGroupMessageSchema = z.object({
  id: z.string().uuid(),
  kind: z.string(),
  content: z.string().nullable(),
  imageUrl: z.string().nullable(),
  senderId: z.string().uuid().nullable(),
  senderName: z.string().nullable(),
  senderAvatarUrl: z.string().nullable(),
  /** Written by the business itself. */
  fromBusiness: z.boolean(),
  deleted: z.boolean(),
  createdAt: z.string(),
  /** For a shared event: what the card shows. */
  event: z
    .object({
      eventId: z.string().uuid(),
      title: z.string(),
      coverImageUrl: z.string().nullable(),
      hasTickets: z.boolean(),
    })
    .nullable(),
  /** For a line like "Anna joined": what happened and who did it. */
  system: z
    .object({
      kind: z.string(),
      actorName: z.string().nullable(),
      targetName: z.string().nullable(),
    })
    .nullable(),
})
export type OrganizerGroupMessage = z.infer<typeof organizerGroupMessageSchema>

export const organizerGroupMessagesQuerySchema = z.object({
  cursor: z.string().optional(),
})

export const organizerGroupMessagesResponseSchema = z.object({
  messages: z.array(organizerGroupMessageSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})

export const organizerGroupMemberSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  joinedAt: z.string(),
})
export type OrganizerGroupMember = z.infer<typeof organizerGroupMemberSchema>

export const organizerGroupMembersResponseSchema = z.object({
  members: z.array(organizerGroupMemberSchema),
})

/** A group people can join from a profile's page in the app. */
export const publicOrganizerGroupSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  memberCount: z.number().int(),
  inviteCode: z.string(),
})
export type PublicOrganizerGroup = z.infer<typeof publicOrganizerGroupSchema>
